import { Portrait, CardOrientationOptions } from 'component-library/cards/Portrait/Portrait'
import { getCardItemProps, LayoutName } from 'component-library/cards/Portrait/Portrait.layouts'
import { CardSwitcher } from 'component-library/__helpers/CardSwitcher'
import { ImageWidths } from 'common/helpers/image'

// Themes, Metrics, Events
import { Product } from 'component-library/__product'
import {
    LandscapeLayout,
    getLandscapeCardItemProps,
} from 'component-library/cards/Landscape/Landscape.layouts'
import { Landscape } from 'component-library/cards/Landscape/Landscape'
import {
    LandscapeStackedLayout,
    getLandscapeStackedCardItemProps,
} from 'component-library/cards/LandscapeStacked/LandscapeStacked.layouts'
import { LandscapeStacked } from 'component-library/cards/LandscapeStacked/LandscapeStacked'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { HeroCard } from 'component-library/cards/HeroCard/HeroCard'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import {
    LimaLayout,
    NovemberLayout,
    CardOverrideOptions,
    LimaLayouts,
    LimaHeroLayout,
    LimaProps,
} from '../Lima'

import React from 'react'
import { StyledGridItem } from '../Lima.styled'
import { StyledNovember } from 'component-library/collections-new/November/StyledNovember'
import { CardItem, isPublicationCardItem } from 'common/client-data-types/card-types'
import { CollectionEvent } from 'common/events/collection-events'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export function isNovemberLayout(layout: LimaLayout): layout is NovemberLayout
export function isNovemberLayout(layout: any) {
    return typeof layout === 'object' && 'type' in layout && layout.type === 'november'
}

export function isLandscapeLayout(layout: any) {
    return typeof 'name' in layout && layout.name === 'landscape'
}

function isHeroLayout(layout: LimaLayout[] | LimaHeroLayout): layout is LimaHeroLayout {
    return 'type' in layout && layout.type === 'hero'
}

export function calculateLayoutsLength(layouts: LimaLayout[] | LimaHeroLayout): number {
    if (isHeroLayout(layouts)) {
        return 1
    }

    return layouts.reduce((accumulator, layout) => {
        const addition = isNovemberLayout(layout) ? layout.listLength : layout.length
        return accumulator + addition
    }, 0)
}

// Switches between Landscape and Landscape Stacked
export function renderLandscapeType(
    layout: LandscapeLayout | LandscapeStackedLayout,
    items: Array<MaybeLoaded<CardItem>>,
    cardNumber: number,
    onEvent: (event: CollectionEvent) => void,
    columnLayoutLength: number,
    disableImageLazyLoad?: boolean,
    timestamp?: TimestampType
) {
    const item = items.length > columnLayoutLength && items[columnLayoutLength]
    if (!item) {
        return null
    }

    switch (layout.type) {
        case 'landscape':
            return (
                <CardSwitcher
                    key={columnLayoutLength}
                    item={item}
                    cardContext="lima-landscape"
                    cardNumber={cardNumber}
                    publicationCard={publicationItem => (
                        <Landscape
                            timestamp={timestamp}
                            key={columnLayoutLength}
                            disableImageLazyLoad={disableImageLazyLoad}
                            {...getLandscapeCardItemProps(
                                publicationItem,
                                layout,
                                { onEvent },
                                cardNumber
                            )}
                        />
                    )}
                />
            )

        case 'landscape-stacked':
            return (
                <CardSwitcher
                    key={columnLayoutLength}
                    item={item}
                    cardContext="lima-landscapestacked"
                    cardNumber={cardNumber}
                    publicationCard={publicationItem => (
                        <LandscapeStacked
                            key={columnLayoutLength}
                            timestamp={timestamp}
                            disableImageLazyLoad={disableImageLazyLoad}
                            {...getLandscapeStackedCardItemProps(
                                publicationItem,
                                layout,
                                { onEvent },
                                cardNumber
                            )}
                        />
                    )}
                />
            )
        default:
            return () => undefined
    }
}

function renderCard(
    layouts: Array<LayoutName | LandscapeLayout | LandscapeStackedLayout>,
    items: Array<MaybeLoaded<CardItem>>,
    prevLayoutsLength: number,
    onEvent: (event: CollectionEvent) => void,
    imageWidths: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews'],
    cardOptionOverrides: CardOverrideOptions,
    fixedRatios?: FixedRatio | FixedRatio[] | CardBreakpointRatios,
    hasBackground?: boolean,
    textVerticalSpacing?: keyof ThemeMargins,
    cardOrientation?: CardOrientationOptions | undefined,
    disableImageLazyLoad?: boolean,
    timestamp?: TimestampType
) {
    return layouts.map(
        (layout: LayoutName | LandscapeLayout | LandscapeStackedLayout, columnIndex: number) => {
            const columnLayoutLength =
                calculateLayoutsLength([layouts.slice(0, columnIndex)]) + prevLayoutsLength
            const cardNumber = columnLayoutLength + 1

            if (!isPortraitCard(layout)) {
                return renderLandscapeType(
                    layout,
                    items,
                    cardNumber,
                    onEvent,
                    columnLayoutLength,
                    disableImageLazyLoad,
                    timestamp
                )
            }

            const item = items.length > columnLayoutLength && items[columnLayoutLength]
            if (!item) {
                return null
            }

            return (
                <CardSwitcher
                    key={columnLayoutLength}
                    item={item}
                    cardContext="lima-portrait"
                    cardNumber={cardNumber}
                    publicationCard={publicationItem => (
                        <Portrait
                            timestamp={timestamp}
                            data-layout={layout}
                            imageWidths={imageWidths}
                            textVerticalSpacing={textVerticalSpacing}
                            hasBackground={hasBackground}
                            disableImageLazyLoad={disableImageLazyLoad}
                            fixedRatio={fixedRatios}
                            {...cardOptionOverrides}
                            {...getCardItemProps(
                                publicationItem,
                                layout,
                                {
                                    onEvent,
                                },
                                cardNumber,
                                cardOrientation
                            )}
                        />
                    )}
                />
            )
        }
    )
}

export function renderCardLayout(
    cardLayout: LimaLayouts,
    cardOverrideOptions: CardOverrideOptions,
    props: LimaProps,
    items: MaybeLoaded<CardItem>[],
    getImageWidth: (itemsCount: number, product: Product) => 500 | 650 | 268 | 320,
    product: Product
): React.ReactNode {
    const {
        hasBackground,
        onEvent,
        initialColumns = 1,
        intermediateColumns = 4,
        finalColumns = 4,
        textVerticalSpacing,
        cardOrientation,
        disableImageLazyLoad,
    } = props

    if (isHeroLayout(cardLayout)) {
        const item: MaybeLoaded<CardItem> =
            props.items.loaded && props.items.result.length > 0
                ? { loaded: true, result: props.items.result[0] }
                : { loaded: false }

        return (
            <StyledGridItem
                numberOfItems={1}
                intermediateColumns={1}
                initialColumns={1}
                finalColumns={1}
            >
                <CardSwitcher
                    cardNumber={0}
                    item={item}
                    cardContext="lima-hero"
                    publicationCard={publicationCardItem => (
                        <HeroCard
                            item={publicationCardItem}
                            fixedRatio={'16:9'}
                            cardNumber={0}
                            onEvent={onEvent}
                            hasBackground
                            imageLayout="fill-container-horizontally"
                        />
                    )}
                />
            </StyledGridItem>
        )
    }

    // Typescript is not narrowing properly after isHeroLayout(cardLayout)
    const cardLayoutLimaOnly = cardLayout as LimaLayout[]

    return cardLayoutLimaOnly.map((layout, layoutIndex) => {
        const prevLayouts = cardLayoutLimaOnly.slice(0, layoutIndex)
        const prevLayoutsLength = calculateLayoutsLength(prevLayouts)
        return (
            <StyledGridItem
                key={layoutIndex}
                numberOfItems={cardLayoutLimaOnly.length}
                intermediateColumns={intermediateColumns}
                initialColumns={initialColumns}
                finalColumns={finalColumns}
            >
                {isNovemberLayout(layout) ? (
                    <StyledNovember
                        hasBackground
                        disableImpressionEvent={true}
                        cardNumberOffset={prevLayoutsLength}
                        sectionHeader={layout.sectionHeader}
                        onEvent={onEvent}
                        timestamp={layout.timestamp}
                        noStretch={layout.noStretch}
                        expectedCards={layout.listLength}
                        items={
                            props.items.loaded
                                ? {
                                      loaded: true,
                                      result: props.items.result
                                          .slice(
                                              prevLayoutsLength,
                                              prevLayoutsLength + layout.listLength
                                          )
                                          // November doesn't support non-publication cards
                                          // so just filter them. If they end up in a november
                                          // index that is probably a bug anyways
                                          .filter(isPublicationCardItem),
                                  }
                                : { loaded: false }
                        }
                    />
                ) : (
                    renderCard(
                        layout,
                        items,
                        prevLayoutsLength,
                        onEvent,
                        getImageWidth(items.length, product),
                        cardOverrideOptions,
                        props.fixedRatios,
                        hasBackground,
                        textVerticalSpacing,
                        cardOrientation,
                        disableImageLazyLoad,
                        props.timestamp
                    )
                )}
            </StyledGridItem>
        )
    })
}

// TODO - when card layouts are updated to be the same we should be able to get rid of this and simplfy the checking
function isPortraitCard(
    layout: LayoutName | LandscapeLayout | LandscapeStackedLayout
): layout is LayoutName {
    return (
        typeof layout === 'string' ||
        !(('type' in layout && layout.type === 'landscape') || layout.type === 'landscape-stacked')
    )
}
